// /write — the locked editor for the user's draft. Until the server exists (E5) the draft lives
// in IndexedDB and is locked to tomorrow's period from the dev fake day.
import { stopRiverIfPlaying } from "../river/context";
import { useEffect, useState } from "react";
import { dayAt, dayKeys, everyEvent, getPreset, KITS, keyName, periodSpec, getAtmosKit, type Key } from "@eb/shared";
import { fetchKit } from "../audio/host";
import { editorAudio, stopPlay } from "../editor/audio";
import { loadDraft, saveDraft } from "../editor/drafts";
import { MixPanel } from "../editor/MixPanel";
import { StagePanel } from "../editor/StagePanel";
import { SongPanel } from "../editor/SongPanel";
import { EditorTourButton } from "../tour/TourLaunch";
import { SoundPicker } from "../editor/SoundPicker";
import { Pads } from "../editor/Pads";
import { loadIntoEditor, setAltKey, useEditor } from "../editor/store";
import { useEditorKeyboard } from "../editor/keyboard";
import { TrackGrid } from "../editor/TrackGrid";
import { Transport } from "../editor/Transport";
import { navigate, useSearch, useTitle } from "../lib/router";
import { riverPlayer } from "../river/context";
import { openServerDraft, saveServerDraft, waitForCheck } from "../editor/serverDraft";
import { SubmitPanel } from "../editor/SubmitPanel";
import { WizardPanel } from "../editor/WizardPanel";
import { PhoneEditor } from "../editor/PhoneEditor";
import { ZooEditor } from "../skins/ZooEditor";
import { useSettings } from "../ui/settings";
import { useMedia } from "../ui/useMedia";
import { ApiError } from "../api";
import { useSession } from "../session";

const LOCAL_DRAFT = "local";

export function Write() {
  useTitle("Write a song");
  const def = useEditor((s) => s.def);
  const user = useSession((s) => s.user);
  const sessionLoaded = useSession((s) => s.loaded);
  const params = useSearch();
  const wantDay = params.get("day");
  const wantDraft = params.get("draft");
  const fresh = params.get("new") === "1";
  const [epochMs, setEpochMs] = useState<number | null>(null);
  const serverMode = !!user?.nickname;
  const phone = useMedia("(max-width: 640px)");
  const skin = useSettings((s) => s.skin);
  const savedAt = useEditor((s) => s.savedAt);
  const dirty = useEditor((s) => s.dirty);
  const events = useEditor((s) => everyEvent(s.hist.present).length);
  const [error, setError] = useState("");

  // Load (or create) the draft: on the server when signed in with a nickname, else locally.
  // Opening the editor stops the River, so it doesn't play over your song.
  useEffect(() => {
    void stopRiverIfPlaying();
  }, []);

  useEffect(() => {
    if (!sessionLoaded) return;
    let alive = true;
    void (async () => {
      const player = await riverPlayer();
      setEpochMs(player.epochMs());
      if (serverMode) {
        const d = await openServerDraft({
          day: wantDay ? Number(wantDay) : undefined,
          draftId: wantDraft ?? undefined,
          fresh,
        });
        if (d.status === "checking") void waitForCheck(d.id);
        // Keep the URL on this draft so a reload shows the same one.
        if (wantDraft !== d.id) navigate(`/write?draft=${encodeURIComponent(d.id)}`, { replace: true });
        return;
      }
      const today = dayAt(player.worldNow(), player.epochMs());
      // A day picked in the calendar opens that day's sounds (one local draft per picked day).
      const picked = wantDay !== null && Number(wantDay) > today ? Number(wantDay) : null;
      const draftId = picked !== null ? `${LOCAL_DRAFT}-${picked}` : LOCAL_DRAFT;
      let existing = await loadDraft(draftId);
      if (!existing && picked !== null) {
        const legacy = await loadDraft(LOCAL_DRAFT);
        if (legacy?.day === picked) existing = legacy;
      }
      const day = picked ?? (existing && existing.day > today ? existing.day : today + 1);
      const pdef = await player.period(day);
      const { manifest } = await fetchKit(pdef.kit);
      if (!alive) return;
      loadIntoEditor(
        draftId,
        pdef,
        periodSpec(pdef, manifest),
        existing?.day === day ? existing.loop : null,
        existing?.title ?? "",
      );
    })().catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
    return () => {
      alive = false;
      void stopPlay();
    };
  }, [sessionLoaded, serverMode, wantDay, wantDraft, fresh]);

  // Autosave to IndexedDB (debounced; flushed when the page is hidden or closed).
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const flush = () => {
      clearTimeout(timer);
      timer = undefined;
      const st = useEditor.getState();
      if (!st.def || !st.draftId || !st.dirty) return;
      const loop = st.hist.present;
      const done = () => {
        if (useEditor.getState().hist.present === loop) useEditor.getState().set({ savedAt: Date.now(), dirty: false });
      };
      // Always keep a device copy; server drafts are also saved on the server.
      const local = saveDraft({ id: st.draftId, day: st.def.day, loop, title: st.title, updatedAt: Date.now() });
      if (st.server)
        void Promise.all([local, saveServerDraft()]).then(done, (e) =>
          setError(e instanceof ApiError ? e.message : String(e)),
        );
      else void local.then(done);
    };
    const unsub = useEditor.subscribe((s, p) => {
      if (s.hist.present === p.hist.present || !s.def || !s.draftId || s.loadSeq !== p.loadSeq) return;
      if (!s.dirty) useEditor.getState().set({ dirty: true });
      clearTimeout(timer);
      timer = setTimeout(flush, 250);
    });
    const onHide = () => flush();
    const onVis = () => document.visibilityState === "hidden" && flush();
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      flush();
      unsub();
      window.removeEventListener("pagehide", onHide);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  useEditorKeyboard();

  if (error)
    return (
      <main id="main" className="page">
        <p role="alert">{error}</p>
      </main>
    );
  if (!def)
    return (
      <main id="main" className="page">
        <p className="muted">Opening your draft…</p>
      </main>
    );

  return (
    <main id="main" className="page page-wide editor-page" onPointerDown={() => void editorAudio()}>
      <section className="editor-head oled" aria-label="Your day">
        <span>
          Writing for <strong data-testid="editor-day">day {def.day}</strong> · <KeySwitch dayKey={def.key} /> ·{" "}
          {KITS[def.kit].genre === KITS[def.kit].name
            ? KITS[def.kit].name
            : `${KITS[def.kit].genre} (${KITS[def.kit].name})`}{" "}
          · {getPreset(def.instruments.bass).name} · {getPreset(def.instruments.harmony).name} ·{" "}
          {getPreset(def.instruments.lead).name} · {getPreset(def.instruments.classic).name} ·{" "}
          {getAtmosKit(def.atmosKit).name} atmos · {def.bpm ?? 125} BPM · 16-bar song
        </span>
        <span className="row editor-head-side">
          <span className="muted-oled" data-testid="save-status">
            {events} notes ·{" "}
            {dirty ? "saving…" : savedAt ? (serverMode ? "saved" : "saved on this device") : "not changed yet"}
          </span>
          <EditorTourButton />
        </span>
      </section>
      <Transport />
      <SongPanel />
      {skin === "zoo" && <ZooEditor />}
      {phone ? (
        <div className="editor-phone">
          <Pads />
          <PhoneEditor />
          <MixPanel />
          <StagePanel />
          <SoundPicker />
        </div>
      ) : (
        <div className="editor-main">
          <TrackGrid />
          <aside className="editor-side">
            <Pads />
            <MixPanel />
            <StagePanel />
            <SoundPicker />
          </aside>
        </div>
      )}
      <div className="editor-below">
        <WizardPanel />
        {epochMs !== null && <SubmitPanel epochMs={epochMs} />}
      </div>
    </main>
  );
}

/** The song's key: the day's own or its relative (the same notes, another home). */
function KeySwitch({ dayKey }: { dayKey: Key }) {
  const alt = useEditor((s) => s.hist.present.alt_key === true);
  const readOnly = useEditor((s) => s.readOnly);
  return (
    <span className="segmented key-switch" role="radiogroup" aria-label="Key of your song" data-testid="key-switch">
      {dayKeys(dayKey).map((k, i) => (
        <button
          key={keyName(k)}
          type="button"
          role="radio"
          aria-checked={alt === (i === 1)}
          className={alt === (i === 1) ? "on" : ""}
          disabled={readOnly}
          onClick={() => setAltKey(i === 1)}
        >
          {keyName(k)}
        </button>
      ))}
    </span>
  );
}
